import apiClient from "@/api/client";
import type {
  AsyncTaskRecord,
  ChangeLogEntry,
  CoefficientSet,
  ContractorTravelInfo,
  CustomPriceEntry,
  EstimateJobDetail,
  EstimateJobList,
  FrameworkAgreementOption,
  JobSummaries,
  KnowledgeNote,
  ManualPricingMode,
  NormCatalogHeader,
  NormFamily,
  NormPosition,
  NormPositionDetail,
  NormSearchHit,
  NormTable,
  Paged,
  RateCatalog,
  RateCatalogEntry,
  RateCatalogStats,
  RebaseResult,
  RevertReport,
  SoilPreview,
  SoilReport,
  ScenarioLineItem,
  SubcontractorScenario,
  TakeoffRule,
  TakeoffRuleFormData,
  TakeoffVariables,
  TenderOutcome,
  Variant,
} from "./types";

// apiClient defaults to application/json; multipart posts must override it.
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } } as const;
const BASE = "/estimating";

async function unwrap<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  const r = await p;
  return r.data.data;
}

export const startEstimateJob = (
  files: File[],
  opts: {
    rms_catalog?: string;
    bcj_catalog?: string;
    coefficient_set?: string;
    offer_deadline?: string;
    tender_url?: string;
  } = {},
) => {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  if (opts.rms_catalog) fd.append("rms_catalog", opts.rms_catalog);
  if (opts.bcj_catalog) fd.append("bcj_catalog", opts.bcj_catalog);
  if (opts.coefficient_set) fd.append("coefficient_set", opts.coefficient_set);
  if (opts.offer_deadline) fd.append("offer_deadline", opts.offer_deadline);
  if (opts.tender_url) fd.append("tender_url", opts.tender_url);
  return unwrap<{ task_id: string; job_id: string }>(
    apiClient.post(`${BASE}/jobs/start/`, fd, MULTIPART),
  );
};

/** Drugie wejście do modułu wycen: zamiast pliku podajemy wariant rysunku przetargu ZE.
 *  Dalej wszystko jest tym samym kosztorysem co przy wgraniu PDF-a. */
export const startTakeoffJob = (
  variant: string,
  opts: {
    rms_catalog?: string;
    bcj_catalog?: string;
    coefficient_set?: string;
  } = {},
) =>
  unwrap<{ task_id: string; job_id: string }>(
    apiClient.post(`${BASE}/jobs/start-takeoff/`, { variant, ...opts }),
  );

// ── słownik reguł obmiaru ────────────────────────────────────────────────────

export const listTakeoffRules = (params?: { element_type?: string; active?: string }) =>
  unwrap<TakeoffRule[]>(apiClient.get(`${BASE}/takeoff-rules/`, { params }));

export const createTakeoffRule = (data: TakeoffRuleFormData) =>
  unwrap<TakeoffRule>(apiClient.post(`${BASE}/takeoff-rules/`, data));

export const updateTakeoffRule = (id: string, data: Partial<TakeoffRuleFormData>) =>
  unwrap<TakeoffRule>(apiClient.patch(`${BASE}/takeoff-rules/${id}/`, data));

export const deleteTakeoffRule = (id: string) =>
  apiClient.delete(`${BASE}/takeoff-rules/${id}/`);

export const getTakeoffVariables = () =>
  unwrap<TakeoffVariables>(apiClient.get(`${BASE}/takeoff-rules/variables/`));

export const getTask = (id: string) =>
  unwrap<AsyncTaskRecord>(apiClient.get(`/bg-tasks/${id}/`));

export const listJobs = () =>
  unwrap<EstimateJobList[]>(apiClient.get(`${BASE}/jobs/`, { params: { page_size: 200 } }));

export const getJob = (id: string) =>
  unwrap<EstimateJobDetail>(apiClient.get(`${BASE}/jobs/${id}/`));

export const recomputeJob = (
  id: string,
  payload: Record<string, string | number>,
) => unwrap<EstimateJobDetail>(apiClient.post(`${BASE}/jobs/${id}/recompute/`, payload));

/** Expand a przedmiar from the KNR/KNNR norm catalog → kosztorys nakładczy
 *  (re-prices, switches to the detailed method) without re-uploading. */
export const expandNormsJob = (id: string) =>
  unwrap<EstimateJobDetail>(apiClient.post(`${BASE}/jobs/${id}/expand-norms/`, {}));

/** Share (remove=true → unshare) this valuation with an internal user.
 *  can_edit=true → udostępnienie z prawem edycji (osoba może przejąć wycenę
 *  i wprowadzać zmiany); ponowny POST z inną wartością przestawia poziom. */
export const shareJob = (
  id: string,
  userId: string,
  remove = false,
  canEdit = false,
) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/jobs/${id}/share/`, {
      user_id: userId,
      remove,
      can_edit: canEdit,
    }),
  );

/** Przenieś nakład między R (robocizna), M (materiał) i S (sprzęt) — rodzaj
 *  decyduje o narzutach (Kp i Z od R+S, Kz od M). Cena nakładu zostaje. */
export const setInputKind = (
  inputId: string,
  kind: "labor" | "material" | "equipment",
) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/inputs/${inputId}/kind/`, { kind }),
  );

/** Internal users who may receive a shared valuation (have estimating access). */
export const getShareableUsers = () =>
  unwrap<{ id: string; name: string }[]>(apiClient.get(`${BASE}/jobs/shareable-users/`));

/** Jawne GOTOWE / powrót do „W przygotowaniu”. */
export const completeJob = (id: string, completed: boolean) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/jobs/${id}/complete/`, { completed }),
  );

/** Oferta złożona w przetargu („Złożony”) / cofnięcie do „Gotowy”. Przy składaniu
 *  można od razu podać, KTÓRA kwota poszła do zamawiającego: wariant (przestawia
 *  też pochodne) i/lub kwotę z formularza ofertowego. */
export const submitJob = (
  id: string,
  submitted: boolean,
  opts?: { variant?: Variant; amount?: string },
) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/jobs/${id}/submit/`, { submitted, ...(opts ?? {}) }),
  );

/** Decyzja „Nie startujemy” (rezygnacja ze złożenia oferty) / jej cofnięcie.
 *  Zdejmuje „Złożony” razem z kwotą oferty; wycena i „Gotowe” zostają. */
export const abandonJob = (id: string, abandoned: boolean) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/jobs/${id}/abandon/`, { abandoned }),
  );

/** Wynik przetargu („Przetarg wygrany” / „Przegrane” / „Anulowane”) — stan końcowy
 *  wyceny. null cofa wynik. Wygrana i przegrana wymagają statusu „Złożony”;
 *  anulowanie (ogłaszane też przed terminem składania) nie wymaga. */
export const setJobOutcome = (id: string, outcome: TenderOutcome | null) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/jobs/${id}/outcome/`, { outcome }),
  );

/** Wariant wartości przetargowej, który składamy jako ofertę (min/śr/maks).
 *  Nie przelicza wyceny — przestawia to, z czego liczą się pochodne (podział wg
 *  kategorii, zł/mb, marża na podwykonawcach, raport). */
export const setJobBidVariant = (id: string, variant: Variant) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/jobs/${id}/bid-variant/`, { variant }),
  );

/** Kwota faktycznie złożona w przetargu (tylko dla statusu „Złożony”). Pusty
 *  string = wróć do kwoty policzonego wariantu. */
export const setJobSubmittedAmount = (id: string, amount: string) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/jobs/${id}/submitted-amount/`, { amount }),
  );

/** Notatka kosztorysanta do przetargu (wolny tekst). */
export const setJobNote = (id: string, note: string) =>
  unwrap<EstimateJobDetail>(apiClient.post(`${BASE}/jobs/${id}/note/`, { note }));

/** Termin złożenia oferty + link do przetargu (wpisywane ręcznie). Wysyłamy
 *  tylko te klucze, które faktycznie zmieniamy. */
export const setJobTenderInfo = (
  id: string,
  payload: { offer_deadline?: string | null; tender_url?: string },
) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/jobs/${id}/tender-info/`, payload),
  );

export const listCoefficientSets = () =>
  unwrap<CoefficientSet[]>(apiClient.get(`${BASE}/coefficient-sets/`, { params: { page_size: 200 } }));

export const listCatalogs = () =>
  unwrap<RateCatalog[]>(apiClient.get(`${BASE}/catalogs/`, { params: { page_size: 200 } }));

export const searchCatalogEntries = (catalogId: string, q: string, kind?: string) =>
  unwrap<RateCatalogEntry[]>(
    apiClient.get(`${BASE}/catalogs/${catalogId}/entries/`, {
      params: { q, kind, page_size: 30 },
    }),
  );

// ── Przeglądarka katalogów: normy KNR/KNNR + cenniki SEKOCENBUD ──────────────

/** Odpowiedź stronicowana: dane z `data`, licznik z `meta.count` (koperta API). */
async function unwrapPaged<T>(
  p: Promise<{ data: { data: T[]; meta: { count?: number } } }>,
): Promise<Paged<T>> {
  const r = await p;
  return { results: r.data.data, count: r.data.meta?.count ?? r.data.data.length };
}

export const getCatalogStats = (catalogId: string) =>
  unwrap<RateCatalogStats>(apiClient.get(`${BASE}/catalogs/${catalogId}/stats/`));

export interface CatalogEntryQuery {
  q?: string;
  kind?: string;
  /** code | description | price_avg | price_min | price_max, z „-” = malejąco. */
  ordering?: string;
  page?: number;
  page_size?: number;
}

/** Przeglądanie cennika — stronicowane po CAŁYM zbiorze (meta.count = trafienia). */
export const listCatalogEntries = (catalogId: string, params: CatalogEntryQuery) =>
  unwrapPaged<RateCatalogEntry>(
    apiClient.get(`${BASE}/catalogs/${catalogId}/entries/`, {
      params: {
        q: params.q || undefined,
        kind: params.kind || undefined,
        ordering: params.ordering || undefined,
        page: params.page,
        page_size: params.page_size ?? 50,
      },
    }),
  );

export const listNormCatalogs = () =>
  unwrap<NormCatalogHeader[]>(apiClient.get(`${BASE}/norm-positions/catalogs/`));

export const listNormFamilies = () =>
  unwrap<NormFamily[]>(apiClient.get(`${BASE}/norm-positions/families/`));

export const listNormTables = (family: string) =>
  unwrap<NormTable[]>(
    apiClient.get(`${BASE}/norm-positions/tables/`, { params: { family } }),
  );

export interface NormPositionQuery {
  family?: string;
  table?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

export const listNormPositions = (params: NormPositionQuery) =>
  unwrapPaged<NormPosition>(
    apiClient.get(`${BASE}/norm-positions/`, {
      params: {
        family: params.family || undefined,
        table: params.table || undefined,
        q: params.q || undefined,
        page: params.page,
        page_size: params.page_size ?? 50,
      },
    }),
  );

export const getNormPosition = (id: string) =>
  unwrap<NormPositionDetail>(apiClient.get(`${BASE}/norm-positions/${id}/`));

export const matchInput = (inputId: string, entryId: string | null) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/inputs/${inputId}/match/`, { entry_id: entryId }),
  );

export const matchPosition = (positionId: string, entryId: string | null) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/positions/${positionId}/match/`, { entry_id: entryId }),
  );

export const setInvestorSupplied = (inputId: string, value: boolean) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/inputs/${inputId}/investor/`, { investor_supplied: value }),
  );

export const setManualPrice = (inputId: string, price: string | null) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/inputs/${inputId}/manual-price/`, { price }),
  );

export const setPositionCategory = (positionId: string, category: string) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/positions/${positionId}/category/`, { category }),
  );

/** Ręczna cena jednostkowa POZYCJI bez nakładów (null = usuń nadpisanie). */
export const setPositionManualPrice = (positionId: string, price: string | null) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/positions/${positionId}/manual-price/`, { price }),
  );

/** Ręczna zmiana obmiaru pozycji z dokumentu (null = przywrócenie obmiaru
 *  z dokumentu). Nakłady R/M/S są skalowane proporcjonalnie i wycena liczona
 *  na nowo. */
export const setPositionQuantity = (positionId: string, quantity: string | null) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/positions/${positionId}/quantity/`, { quantity }),
  );

/** Dostawa inwestorska całej pozycji bez nakładów (wycena jawnie na 0 zł). */
export const setPositionInvestorSupplied = (positionId: string, value: boolean) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/positions/${positionId}/investor/`, {
      investor_supplied: value,
    }),
  );

/** Ręczna zmiana ILOŚCI nakładu (kolumna „Nakłady”: r-g robocizny, szt materiału).
 *  Wartość nakładu = ilość × cena jednostkowa, więc zwiększenie podnosi cenę
 *  pozycji. null = przywrócenie ilości z dokumentu. */
export const setInputNorm = (inputId: string, normTotal: string | null) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/inputs/${inputId}/norm/`, { norm_total: normTotal }),
  );

export interface ManualInputPayload {
  kind: "labor" | "material" | "equipment";
  description: string;
  unit?: string;
  norm_total?: string;
  /** Cena jednostkowa — pusta przy robociźnie oznacza stawkę r-g z narzutów wyceny. */
  price?: string;
  comment?: string;
}

/** Dopisanie nakładu R/M/S pod pozycją dodaną ręcznie (tryb „z nakładów”). */
export const createManualInput = (positionId: string, payload: ManualInputPayload) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/positions/${positionId}/manual-inputs/`, payload),
  );

/** Usunięcie nakładu DOPISANEGO RĘCZNIE (wiersz z dokumentu tylko się wykreśla). */
export const deleteManualInput = (inputId: string) =>
  unwrap<EstimateJobDetail>(apiClient.delete(`${BASE}/inputs/${inputId}/manual/`));

/** Wykreślenie nakładu (liczony po 0 zł, widoczny dla śladu audytowego). */
export const setInputExcluded = (inputId: string, excluded: boolean) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/inputs/${inputId}/exclude/`, { excluded }),
  );

/** Narzuty pozycji ryczałtowej: „material” = tylko Kz, „labor” = Kp + Z. */
export const setPositionWholeMarkup = (
  positionId: string,
  mode: "material" | "labor",
) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/positions/${positionId}/whole-markup/`, { mode }),
  );

async function unwrapWithMeta<T, M>(
  p: Promise<{ data: { data: T; meta: M } }>,
): Promise<{ data: T; meta: M }> {
  const r = await p;
  return { data: r.data.data, meta: r.data.meta };
}

/** Zmiana podstawy (kodu KNNR/KNR) pozycji: nakłady R/M/S zaciągane na nowo
 *  z katalogu norm i przeliczane. 400 z errors.variants = warianty tablicy. */
export const setPositionBasis = (positionId: string, basis: string) =>
  unwrapWithMeta<EstimateJobDetail, { rebase: RebaseResult }>(
    apiClient.post(`${BASE}/positions/${positionId}/basis/`, { basis }),
  );

/** Zmiana kategorii gruntu pozycji (podmiana wariantu KNNR + przeliczenie). */
export const setPositionSoilCategory = (positionId: string, category: string) =>
  unwrapWithMeta<EstimateJobDetail, { rebase: RebaseResult }>(
    apiClient.post(`${BASE}/positions/${positionId}/soil-category/`, { category }),
  );

/** Zbiorcza zmiana kategorii gruntu w całej wycenie lub wskazanych pozycjach
 *  (raport z batch_id w meta.soil). */
export const setJobSoilCategory = (
  jobId: string,
  category: string,
  positionIds?: string[],
) =>
  unwrapWithMeta<EstimateJobDetail, { soil: SoilReport }>(
    apiClient.post(`${BASE}/jobs/${jobId}/soil-category/`, {
      category,
      ...(positionIds ? { position_ids: positionIds } : {}),
    }),
  );

/** Dry-run kaskady: diff „przed / po” bez zapisu (przeliczenie w transakcji
 *  wycofywanej na końcu). */
export const previewJobSoilCategory = async (
  jobId: string,
  category: string,
  positionIds?: string[],
): Promise<SoilPreview> => {
  const r = await apiClient.post(`${BASE}/jobs/${jobId}/soil-category/`, {
    category,
    preview: true,
    ...(positionIds ? { position_ids: positionIds } : {}),
  });
  return r.data.meta.soil_preview as SoilPreview;
};

/** Dziennik zmian podstawy/kategorii gruntu wyceny (najnowsze pierwsze). */
export const listChangeLog = (jobId: string) =>
  unwrap<ChangeLogEntry[]>(apiClient.get(`${BASE}/jobs/${jobId}/change-log/`));

/** Wycofanie zmiany: log_id = jedna pozycja, batch_id = cała kaskada. */
export const revertChange = (
  jobId: string,
  payload: { log_id?: string; batch_id?: string },
) =>
  unwrapWithMeta<EstimateJobDetail, { revert: RevertReport }>(
    apiClient.post(`${BASE}/jobs/${jobId}/revert-change/`, payload),
  );

/** Podpowiedzi z katalogu norm KNR/KNNR (kod lub fragment opisu, min. 3 znaki). */
export const searchNormPositions = (q: string) =>
  unwrap<NormSearchHit[]>(apiClient.get(`${BASE}/norms/`, { params: { q } }));

/** Ręczna zmiana nazwy pozycji (puste = przywrócenie opisu z dokumentu). */
export const setPositionDescription = (positionId: string, description: string) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/positions/${positionId}/description/`, { description }),
  );

/** Ręczna zmiana nazwy nakładu R/M/S (puste = przywrócenie nazwy z dokumentu). */
export const setInputDescription = (inputId: string, description: string) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/inputs/${inputId}/description/`, { description }),
  );

/** Komentarz kosztorysanta przy pozycji / nakładzie (puste = usunięcie). */
export const setPositionComment = (positionId: string, comment: string) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/positions/${positionId}/comment/`, { comment }),
  );

export const setInputComment = (inputId: string, comment: string) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/inputs/${inputId}/comment/`, { comment }),
  );

export interface ManualPositionPayload {
  description?: string;
  unit?: string;
  quantity?: string;
  /** Cena jednostkowa — ujemna dopuszczalna (korekta w dół). */
  price?: string;
  pricing_mode?: ManualPricingMode;
  category?: string;
  comment?: string;
  /** Pozycja wzięta z „Naszej bazy cen” — uzupełnia puste pola i zostaje jako źródło. */
  entry_id?: string;
}

/** Dopisanie pozycji ręcznie do kosztorysu (i przeliczenie wyceny). */
export const createManualPosition = (
  estimateId: string,
  payload: ManualPositionPayload,
) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/estimates/${estimateId}/manual-positions/`, payload),
  );

export const updateManualPosition = (
  positionId: string,
  payload: ManualPositionPayload,
) =>
  unwrap<EstimateJobDetail>(
    apiClient.patch(`${BASE}/positions/${positionId}/manual/`, payload),
  );

export const deleteManualPosition = (positionId: string) =>
  unwrap<EstimateJobDetail>(
    apiClient.delete(`${BASE}/positions/${positionId}/manual/`),
  );

export const setRouteLength = (
  estimateId: string,
  payload: {
    route_open_trench_m?: string | null;
    route_bore_m?: string | null;
    route_length_m?: string | null;
  },
) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/estimates/${estimateId}/route-length/`, payload),
  );

export const aiPriceInput = (inputId: string, clear = false) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(`${BASE}/inputs/${inputId}/ai-price/`, clear ? { clear: true } : {}),
  );

export const aiPricePosition = (positionId: string, clear = false) =>
  unwrap<EstimateJobDetail>(
    apiClient.post(
      `${BASE}/positions/${positionId}/ai-price/`,
      clear ? { clear: true } : {},
    ),
  );

export const aiPriceUnmatched = (jobId: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/jobs/${jobId}/ai-price-unmatched/`, {}),
  );

export type AiPricePreview = {
  positions: number;
  materials: number;
  to_price: number;
  skipped: number;
  cap: number;
  model: string;
  max_searches: number;
  estimated_cost_usd: string;
};

/** What a market-pricing run would cover and cost — spends nothing. */
export const aiPricePreview = (jobId: string) =>
  unwrap<AiPricePreview>(
    apiClient.get(`${BASE}/jobs/${jobId}/ai-price-unmatched/`),
  );

// Headless AI review ("presetowany ARON"): structured risks/inconsistencies on top of
// the deterministic warnings. Run is a BACKGROUND task → poll task_id (the call is slow).
export const aiReviewRun = (jobId: string) =>
  unwrap<{ task_id: string }>(apiClient.post(`${BASE}/jobs/${jobId}/ai-review/`, {}));

export const aiReviewClear = (jobId: string) =>
  unwrap<EstimateJobDetail>(apiClient.post(`${BASE}/jobs/${jobId}/ai-review/`, { clear: true }));

// ── Baza wiedzy PRZ-ARON (notatki tekstowe uczące AI) ────────────────────────

export const listKnowledgeNotes = () =>
  unwrap<KnowledgeNote[]>(
    apiClient.get(`${BASE}/knowledge-notes/`, { params: { page_size: 200 } }),
  );

export const createKnowledgeNote = (body: string) =>
  unwrap<KnowledgeNote>(apiClient.post(`${BASE}/knowledge-notes/`, { body }));

export const updateKnowledgeNote = (
  id: string,
  payload: { body?: string; is_active?: boolean },
) => unwrap<KnowledgeNote>(apiClient.patch(`${BASE}/knowledge-notes/${id}/`, payload));

export const deleteKnowledgeNote = (id: string) =>
  apiClient.delete(`${BASE}/knowledge-notes/${id}/`);

// ── Nasza baza cen (własne pozycje cenowe) ───────────────────────────────────

export interface CustomPricePayload {
  kind?: "material" | "equipment" | "work_unit";
  description?: string;
  unit?: string;
  price_avg?: string | null;
  price_min?: string | null;
  price_max?: string | null;
  note?: string;
  source_url?: string;
}

export const listCustomPrices = (q?: string, kind?: string) =>
  unwrap<CustomPriceEntry[]>(
    apiClient.get(`${BASE}/custom-prices/`, {
      params: { q: q || undefined, kind: kind || undefined, page_size: 500 },
    }),
  );

export const createCustomPrice = (payload: CustomPricePayload) =>
  unwrap<CustomPriceEntry>(apiClient.post(`${BASE}/custom-prices/`, payload));

export const updateCustomPrice = (id: string, payload: CustomPricePayload) =>
  unwrap<CustomPriceEntry>(
    apiClient.patch(`${BASE}/custom-prices/${id}/`, payload),
  );

export const deleteCustomPrice = (id: string) =>
  apiClient.delete(`${BASE}/custom-prices/${id}/`);

// ── Subcontractor pricing (Layer B + margin) ─────────────────────────────────

export const listFrameworkAgreements = () =>
  unwrap<FrameworkAgreementOption[]>(
    apiClient.get(`${BASE}/framework-agreements/`),
  );

export const listScenarios = (jobId: string) =>
  unwrap<SubcontractorScenario[]>(
    apiClient.get(`${BASE}/subcontractor-scenarios/`, { params: { job: jobId } }),
  );

export const getScenario = (sid: string) =>
  unwrap<SubcontractorScenario>(
    apiClient.get(`${BASE}/subcontractor-scenarios/${sid}/`),
  );

export const createScenario = (jobId: string, name: string, agreement_ids: string[]) =>
  unwrap<SubcontractorScenario>(
    apiClient.post(`${BASE}/subcontractor-scenarios/`, {
      job: jobId,
      name,
      agreement_ids,
    }),
  );

export const updateScenario = (
  sid: string,
  payload: { name?: string; agreement_ids?: string[]; ai_rule_text?: string },
) =>
  unwrap<SubcontractorScenario>(
    apiClient.patch(`${BASE}/subcontractor-scenarios/${sid}/`, payload),
  );

export const deleteScenario = (sid: string) =>
  apiClient.delete(`${BASE}/subcontractor-scenarios/${sid}/`);

export const aiMatchScenario = (sid: string) =>
  unwrap<{
    scenario: SubcontractorScenario;
    result: {
      created: number;
      error?: string;
      need_build_location?: boolean;
      uncovered_ordinals?: number[];
    };
  }>(apiClient.post(`${BASE}/subcontractor-scenarios/${sid}/ai-match/`, {}));

export const scenarioLineItems = (sid: string) =>
  unwrap<ScenarioLineItem[]>(
    apiClient.get(`${BASE}/subcontractor-scenarios/${sid}/line-items/`),
  );

export const addAssignment = (
  sid: string,
  payload: {
    line_item_type_id: string;
    covered_position_ids: string[];
    quantity?: string;
    use_high_priority?: boolean;
  },
) =>
  unwrap<SubcontractorScenario>(
    apiClient.post(`${BASE}/subcontractor-scenarios/${sid}/assignments/`, payload),
  );

export const patchAssignment = (
  aid: string,
  payload: {
    quantity?: string;
    rate?: string;
    use_high_priority?: boolean;
    is_alternative?: boolean;
    note?: string;
  },
) =>
  unwrap<SubcontractorScenario>(
    apiClient.patch(`${BASE}/subcontractor-assignments/${aid}/`, payload),
  );

export const deleteAssignment = (aid: string) =>
  unwrap<SubcontractorScenario>(
    apiClient.delete(`${BASE}/subcontractor-assignments/${aid}/`),
  );

/** Przypisz pozycję do wiersza podwykonawcy. Bez udziału = całość (100%);
 *  `amount` = ile złotych naszej wartości pozycji bierze ten podwykonawca
 *  (serwer przelicza na udział), `fraction` = ten sam podział ułamkiem. */
export const setCoverage = (
  aid: string,
  position_id: string,
  fraction?: string,
  amount?: string,
) =>
  unwrap<SubcontractorScenario>(
    apiClient.post(`${BASE}/subcontractor-assignments/${aid}/coverage/`, {
      position_id,
      fraction,
      amount,
    }),
  );

export const removeCoverage = (aid: string, position_id: string) =>
  unwrap<SubcontractorScenario>(
    apiClient.delete(`${BASE}/subcontractor-assignments/${aid}/coverage/`, {
      data: { position_id },
    }),
  );

export const setBuildLocation = (
  sid: string,
  payload: { lat: string | null; lng: string | null; label?: string },
) =>
  unwrap<SubcontractorScenario>(
    apiClient.post(`${BASE}/subcontractor-scenarios/${sid}/build-location/`, payload),
  );

export interface BuildLocationGeocodeHit {
  lat: number;
  lng: number;
  /** Formatted match (address or "dz. 121/2 (Gmina, obręb …)") shown to the user. */
  label: string;
  kind: "address" | "parcel";
  accuracy?: number;
}
/** Address free text or full parcel TERYT → candidate points for the build-location pin. */
export const geocodeBuildLocation = (q: string) =>
  unwrap<BuildLocationGeocodeHit[]>(
    apiClient.get(`${BASE}/geocode/`, { params: { q } }),
  );

export const scenarioContractors = (sid: string) =>
  unwrap<ContractorTravelInfo[]>(
    apiClient.get(`${BASE}/subcontractor-scenarios/${sid}/contractors/`),
  );

export const addContractorBase = (
  contractorId: string,
  payload: { label: string; city: string },
) =>
  apiClient.post(`${BASE}/contractors/${contractorId}/bases/`, payload);

export const deleteContractorBase = (baseId: string) =>
  apiClient.delete(`${BASE}/contractor-bases/${baseId}/`);

// Fresh presigned URL of an uploaded source file (job.files / job.documents
// carry only the ManagedFile id — the S3 link expires, so it is fetched at
// click time instead of being cached inside the job detail).
export const getManagedFileUrl = (managedFileId: string) =>
  unwrap<{ url: string | null; original_filename: string }>(
    apiClient.get(`/files/${managedFileId}/`),
  );

// Expanded valuation report (PDF) with warnings. Fetched as a blob through the
// authenticated client so the JWT header is sent (a plain <a href> can't carry it).
export const downloadJobPdf = async (id: string): Promise<Blob> => {
  const r = await apiClient.get(`${BASE}/jobs/${id}/pdf/`, {
    responseType: "blob",
  });
  return r.data as Blob;
};

// ── Zestawienie materiałów / sprzętu + rozeznanie rynku (AI) ─────────────────

/** Obie tabele liczone w locie z wycenionych nakładów + zapisane oferty AI.
 *  `threshold` steruje tylko domyślnym zaznaczeniem wierszy do rozeznania. */
export const getJobSummaries = (jobId: string, threshold?: string) =>
  unwrap<JobSummaries>(
    apiClient.get(`${BASE}/jobs/${jobId}/summaries/`, {
      params: threshold ? { threshold } : undefined,
    }),
  );

/** Uruchamia rozeznanie rynku dla wskazanych materiałów (brak keys = domyślne).
 *  Wynik NIE zmienia wartości oferty przetargowej. Zwraca task_id do odpytania. */
export const scanMaterialOffers = (jobId: string, keys?: string[]) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/jobs/${jobId}/material-offers/`, keys ? { keys } : {}),
  );

export const clearMaterialOffers = (jobId: string) =>
  unwrap<{ cleared: boolean }>(
    apiClient.post(`${BASE}/jobs/${jobId}/material-offers/`, { clear: true }),
  );

/** Ponowne wyszukanie warunków wyłączeń w dokumentach zlecenia (zadanie w tle).
 *  Analiza wgranych plików robi to sama — ta akcja jest dla starszych wycen. */
export const rescanOutages = (jobId: string) =>
  unwrap<{ task_id: string }>(apiClient.post(`${BASE}/jobs/${jobId}/outages/`, {}));

/** Ponowne wyszukanie WYKAZU dostawy inwestorskiej w specyfikacji + wyzerowanie
 *  odpowiadających mu nakładów (zadanie w tle). ZMIENIA kwoty. Analiza wgranych
 *  plików robi to sama — ta akcja jest dla starszych wycen i po dorzuceniu
 *  specyfikacji do istniejącej paczki. */
export const rescanInvestorSupply = (jobId: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/jobs/${jobId}/investor-supply/`, {}),
  );

/** Weryfikacja wyceny z DOKUMENTACJĄ projektową — odczyt urządzeń z opisu/
 *  schematów/rysunków i porównanie ich z wyceną (zadanie w tle). Analiza
 *  wgranych plików robi to sama; akcja jest dla starszych wycen, po dorzuceniu
 *  projektu i po ręcznych poprawkach pozycji. */
export const runDeviceCheck = (jobId: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/jobs/${jobId}/device-check/`, {}),
  );

/** Bieżąca (wspólna) wycena z rysunku dla przetargu — null, gdy jeszcze nikt nie wyceniał. */
export const getTenderTakeoffJob = (tender: string) =>
  unwrap<EstimateJobDetail | null>(
    apiClient.get(`${BASE}/jobs/for-tender/`, { params: { tender } }),
  );
